import { formatListWithAnd } from "@src/appV2/lib/utils/formatListWithAnd";
import { capitalize, groupBy, startCase } from "lodash";
import { type z } from "zod";

import type { PlacementWithDetails } from "../api/useFetchPaginatedPlacements";
import {
  type GetPlacementsResponse,
  type placementDataSchema,
  type PlacementIncludedTag,
} from "../types/fetchPlacements.schema";

// Extracts and transforms placement tags from the included relationship data
export function extractPlacementTags(
  placement: z.infer<typeof placementDataSchema>,
  placementIncludedMetaData: GetPlacementsResponse["included"]
) {
  const tagMetadata = placementIncludedMetaData.filter(
    (included): included is PlacementIncludedTag =>
      included.type === "tag" &&
      (placement.relationships.tags?.data?.some((tag) => tag.id === included.id) ?? false)
  );

  const transformedTags = tagMetadata.map((tag) => ({ ...tag.attributes, id: tag.id }));

  // Deduplicate by ID
  // Ticket to investigate: https://linear.app/clipboardhealth/issue/GRO-914/investigate-duplicate-tags-in-the-relationship-portion-of-the-response
  return transformedTags.filter(
    (tag, index, self) => self.findIndex((t) => t.id === tag.id) === index
  );
}

// Groups and formats tags for displaying in the UI
export function formatTagsByGroup(
  tags: PlacementWithDetails["tags"]
): Array<{ groupLabel: string; formattedTags: string }> {
  if (!tags || tags.length === 0) {
    return [];
  }

  const groupedTags = groupBy(tags, (tag) => tag.metadata?.filterGroup);
  return Object.entries(groupedTags).map(([filterGroup, groupTags]) => {
    const groupLabel = filterGroup ? startCase(filterGroup) : "Tags";
    const tagNames = groupTags.map((tag) => capitalize(tag.name));
    const formattedTags = formatListWithAnd(tagNames);

    return { groupLabel, formattedTags };
  });
}
